import React from "react";

import AndroidIcon from "@common/assets/images/details-pane-icons/operating-systems/mini-icon-android@2x.png";
import iOSIcon from "@common/assets/images/details-pane-icons/operating-systems/mini-icon-ios@2x.png";
import {
  AppVersionIcon,
  BrowserNameIcon,
  BrowserVersionIcon,
  BuildNumberIcon,
  CurrentViewIcon,
  DeviceIcon,
  OSIcon,
  OSNameIcon,
} from "@common/components/DetailsPane/icons";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { userFeedbackAttributes } from "@common/hooks/filtering/modules/useFeedbackAttributes";
import { BasicsPopoverSearch } from "@main/pages/shared/components/BasicsPopoverSearch/BasicsPopoverSearch";
import * as Styled from "@main/pages/UserFeedback/views/UserFeedbackAddNewTicketView/BasicsItem/BasicsItem.styles";
import { useBasicsItemPopover } from "@main/pages/UserFeedback/views/UserFeedbackAddNewTicketView/BasicsItem/useBasicsItemPopover";

export interface BasicsItem {
  key: string;
  label: string;
  value: string;
}

interface BasicsItemProps {
  style?: React.CSSProperties;
  basicsItem: BasicsItem;
  icon: string | React.ComponentType;
  enabled?: boolean;
  onValueSelected: (item: BasicsItem) => void;
}

export const BasicsItem = ({ style, icon, onValueSelected, enabled = true, basicsItem }: BasicsItemProps) => {
  const popover = useBasicsItemPopover({
    attributeName: basicsItem.key,
  });

  const results: string[] = popover.availableFilterHits.map((filter) => filter.value);

  const onPopoverValueSelected = (value: string) => {
    popover.setQuery("");
    popover.setOpen(false);
    (document.activeElement as HTMLElement)?.blur();
    onValueSelected({ ...basicsItem, value: value });
  };

  const isFilled: boolean = Boolean(basicsItem.value) || Boolean(!enabled);

  return (
    <Tooltip
      text={!enabled ? basicsItem.label : `Click to add ${basicsItem.label}`}
      position={"top"}
    >
      <Styled.BasicsItem
        ref={!enabled ? undefined : popover.reference}
        $enabled={enabled}
        style={style}
        {...(enabled ? popover.getReferenceProps() : {})}
      >
        {typeof icon === "string" ? (
          <Styled.BasicsImage src={icon} />
        ) : (
          <Styled.BasicsIcon
            as={icon}
            $isFilled={isFilled}
          />
        )}
        <Styled.BasicsLabel
          p2
          $isFilled={isFilled}
        >
          {basicsItem.value ? basicsItem.value : basicsItem.label}
        </Styled.BasicsLabel>
        {popover.open && (
          <BasicsPopoverSearch
            query={popover.query}
            setQuery={popover.setQuery}
            error={popover.error}
            loading={popover.loading}
            results={results}
            onValueSelected={onPopoverValueSelected}
            popover={popover}
          />
        )}
      </Styled.BasicsItem>
    </Tooltip>
  );
};

export const getBasicsItemIcon = (key: string) => {
  if (key === userFeedbackAttributes.device) return DeviceIcon;
  if (key === userFeedbackAttributes.osName) return OSNameIcon;
  if (key === userFeedbackAttributes.osVersion) return OSIcon;
  if (key === userFeedbackAttributes.appVersion) return AppVersionIcon;
  if (key === userFeedbackAttributes.buildNumber) return BuildNumberIcon;
  if (key === userFeedbackAttributes.browserName) return BrowserNameIcon;
  if (key === userFeedbackAttributes.browserVersion) return BrowserVersionIcon;
  if (key === userFeedbackAttributes.currentView) return CurrentViewIcon;
  if (key === "os_android") return AndroidIcon;
  if (key === "os_ios") return iOSIcon;
  return OSIcon;
};
